import React from 'react';
import { Seo } from '../../components/Seo';

const Privacy: React.FC = () => {
  return (
    <div className="pt-24">
      <Seo
        title="Privacy Policy | Cimmple Software"
        description="How Cimmplex Innovations LLP collects, uses, and protects information when you visit cimmple.com or use CimmpleFlow, CimmplePay, or CimmpleRx."
        keywords={['Cimmple privacy policy', 'CimmpleFlow', 'CimmplePay']}
      />
      {/* Hero Section */}
      <section className="pt-8 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-3">
            Privacy Policy
          </h1>
          <p className="text-lg text-slate-700 dark:text-dark-300">
            Last updated: December 1, 2024
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card p-8 md:p-12 prose prose-slate dark:prose-invert max-w-none">
            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4">Introduction</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              Cimmplex Innovations LLP ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services, including CimmpleFlow and CimmplePay.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Information We Collect</h2>
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">Personal Information</h3>
            <p className="text-slate-700 dark:text-dark-300 mb-4">
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc list-inside text-slate-700 dark:text-dark-300 mb-6 space-y-2">
              <li>Register for an account</li>
              <li>Subscribe to our services</li>
              <li>Fill out a contact form</li>
              <li>Sign up for our newsletter</li>
              <li>Request customer support</li>
            </ul>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              This information may include your name, email address, phone number, company name, billing address, and payment information.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">Automatically Collected Information</h3>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              When you access our website or services, we may automatically collect certain information, including your IP address, browser type, operating system, device information, pages visited, and the date and time of your visit.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">How We Use Your Information</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside text-slate-700 dark:text-dark-300 mb-6 space-y-2">
              <li>Provide, maintain, and improve our services</li>
              <li>Process transactions and send related information</li>
              <li>Send administrative information and updates</li>
              <li>Respond to your comments, questions, and requests</li>
              <li>Send marketing and promotional communications (with your consent)</li>
              <li>Monitor and analyze trends, usage, and activities</li>
              <li>Detect, investigate, and prevent fraudulent transactions and abuse</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Information Sharing and Disclosure</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-4">
              We may share your information in the following situations:
            </p>
            <ul className="list-disc list-inside text-slate-700 dark:text-dark-300 mb-6 space-y-2">
              <li><strong>Service Providers:</strong> We may share information with third-party vendors who perform services on our behalf</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>With Your Consent:</strong> When you have given us permission to share your information</li>
            </ul>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Data Security</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Data Retention</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              We retain your personal information for as long as necessary to fulfill the purposes for which it was collected, including to satisfy legal, accounting, or reporting requirements.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Your Rights</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-4">
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul className="list-disc list-inside text-slate-700 dark:text-dark-300 mb-6 space-y-2">
              <li>The right to access your personal information</li>
              <li>The right to correct inaccurate information</li>
              <li>The right to delete your personal information</li>
              <li>The right to restrict processing</li>
              <li>The right to data portability</li>
              <li>The right to object to processing</li>
            </ul>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Cookies and Tracking Technologies</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              We use cookies and similar tracking technologies to collect information about your browsing activities. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Third-Party Links</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Children's Privacy</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Changes to This Privacy Policy</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Contact Us</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-4">
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <p className="text-slate-700 dark:text-dark-300">
              <strong>Cimmplex Innovations LLP</strong><br />
              Email: <a href="mailto:contact@cimmple.com" className="text-primary-600 dark:text-primary-400 hover:underline">contact@cimmple.com</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;

